import { useTranslation } from 'react-i18next'
import { Globe } from 'lucide-react'
import { Button } from './Button'
import { Dropdown, DropdownTrigger, DropdownMenu, DropdownItem } from './Dropdown'

const LANGUAGE_ORDER = ['ro', 'en', 'fr'] as const
type Lang = (typeof LANGUAGE_ORDER)[number]

const LANG_LABELS: Record<Lang, string> = {
  ro: 'Română',
  en: 'English',
  fr: 'Français',
}

export interface AnnouncedLanguage {
  /** Only used as a list key — it is never passed to i18n. */
  code: string
  /** Written in the language itself, including its own "coming soon". */
  label: string
}

interface Props {
  onChange?: (lang: string) => void
  /**
   * Languages to show as disabled entries under the real ones, for markets we
   * have not shipped yet. Opt-in, so the gym and member apps do not offer a
   * language their users cannot actually select.
   */
  announced?: readonly AnnouncedLanguage[]
}

export function LanguageSwitcher({ onChange, announced = [] }: Props = {}) {
  const { i18n } = useTranslation()

  const loaded = Object.keys(i18n.options?.resources ?? {})
  const available = LANGUAGE_ORDER.filter((lang) => loaded.includes(lang))
  const languages = available.length > 0 ? available : LANGUAGE_ORDER

  const fallbackLng = i18n.options?.fallbackLng
  const fallback =
    typeof fallbackLng === 'string' && languages.includes(fallbackLng as Lang)
      ? (fallbackLng as Lang)
      : languages[0]

  const lang = i18n.resolvedLanguage ?? i18n.language
  const current = (languages.includes(lang as Lang) ? lang : fallback) as Lang

  function handleChange(lang: Lang) {
    void i18n.changeLanguage(lang)
    onChange?.(lang)
  }

  // Disabled items never fire onAction, so an announced language can't reach i18n.
  const announcedKeys = announced.map((entry) => entry.code)

  return (
    <Dropdown placement="bottom-end">
      <DropdownTrigger>
        <Button isIconOnly size="sm" variant="flat" aria-label="Language">
          <Globe className="h-4 w-4" />
        </Button>
      </DropdownTrigger>
      <DropdownMenu
        aria-label="Language"
        selectionMode="single"
        selectedKeys={[current]}
        disabledKeys={announcedKeys}
        onAction={(key) => handleChange(key as Lang)}
      >
        {[
          ...languages.map((lang) => <DropdownItem key={lang}>{LANG_LABELS[lang]}</DropdownItem>),
          ...announced.map((entry) => (
            <DropdownItem key={entry.code} className="opacity-60">
              {entry.label}
            </DropdownItem>
          )),
        ]}
      </DropdownMenu>
    </Dropdown>
  )
}
